/**
 * Location API Service
 * City suggestions: local list + Nominatim (OSM)
 * Street suggestions: Supabase (lokalna baza) → Google Places → Photon → Nominatim
 * Districts: Overpass API (OSM)
 */

import { supabase } from './supabaseService';

// Cache dla wyników API - poprawia wydajność
const apiCache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minut

const getCachedResult = (key) => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedResult = (key, data) => {
  apiCache.set(key, { data, timestamp: Date.now() });
};

// Bounding box obejmujący całą Polskę – używany do ograniczenia wyników Photon
const POLAND_BBOX = '14.07,49.00,24.15,54.84'; // minLon,minLat,maxLon,maxLat

/**
 * Normalize Polish text - remove diacritics for better matching
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
export const normalizePolishText = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    // Polish specific character mappings
    .replace(/ą/g, 'a')
    .replace(/ć/g, 'c')
    .replace(/ę/g, 'e')
    .replace(/ł/g, 'l')
    .replace(/ń/g, 'n')
    .replace(/ó/g, 'o')
    .replace(/ś/g, 's')
    .replace(/ź/g, 'z')
    .replace(/ż/g, 'z')
    .trim();
};

/**
 * Fetch Polish cities based on search query
 * @param {string} query - Partial city name
 * @returns {Promise<Array>} - Array of city suggestions
 */
export const fetchPolishCities = async (query = '') => {
  if (query.length < 2) {
    return getPopularCities().slice(0, 10);
  }

  const localCities = getPopularCities(query);

  try {
    const apiCities = await fetchCitiesNominatim(query);
    const localNames = new Set(localCities.map(c => normalizePolishText(c.name)));
    const normalizedQuery = normalizePolishText(query);
    const uniqueApiCities = apiCities
      .filter(c => !localNames.has(normalizePolishText(c.name)))
      .filter(c => normalizePolishText(c.name).startsWith(normalizedQuery));
    const merged = [...localCities, ...uniqueApiCities];
    return merged.slice(0, 15);
  } catch (e) {
    return localCities.slice(0, 15);
  }
};

// ─── Google Places Autocomplete ──────────────────────────────────────────────

// ─── Supabase lokalna baza ulic (najszybsza) ────────────────────────────────
const getStreetsLocal = async (cityName, streetQuery) => {
  if (!cityName || streetQuery.length < 2) return null;

  const cacheKey = `streets_local_${normalizePolishText(cityName)}_${normalizePolishText(streetQuery)}`;
  const cached = getCachedResult(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase.rpc('search_streets', {
      p_city: cityName,
      p_query: streetQuery,
      p_limit: 10,
    });

    if (error || !data || data.length === 0) return null;

    const results = data.map((row, idx) => ({
      id: `local_${idx}`,
      name: row.street_name,
      displayName: row.street_name,
      fullAddress: `${row.street_name}, ${row.street_city}`,
    }));

    setCachedResult(cacheKey, results);
    return results;
  } catch {
    return null;
  }
};

// ─── Google Places Autocomplete ──────────────────────────────────────────────
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

let _googleMapsLoading = false;
let _googleMapsResolvers = [];
let _googleMapsError = false; // jeśli raz nie załadował, nie próbuj znowu

const loadGoogleMaps = () =>
  new Promise((resolve, reject) => {
    if (_googleMapsError) { reject(new Error('Google Maps previously failed')); return; }
    if (window.google?.maps?.places) { resolve(); return; }
    _googleMapsResolvers.push({ resolve, reject });
    if (_googleMapsLoading) return;
    _googleMapsLoading = true;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&language=pl`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      _googleMapsResolvers.forEach(r => r.resolve());
      _googleMapsResolvers = [];
    };
    script.onerror = (err) => {
      _googleMapsError = true;
      _googleMapsResolvers.forEach(r => r.reject(err));
      _googleMapsResolvers = [];
    };
    document.head.appendChild(script);
  });

const getStreetsGoogle = async (cityName, streetQuery) => {
  if (!GOOGLE_API_KEY) return null;
  try {
    await loadGoogleMaps();
    if (!window.google?.maps?.places) return null; // script loaded but places not available

    return new Promise((resolve) => {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: `${streetQuery}, ${cityName}`,
          componentRestrictions: { country: 'pl' },
          types: ['route'],
          language: 'pl',
        },
        (predictions, status) => {
          const { OK, ZERO_RESULTS } = window.google.maps.places.PlacesServiceStatus;
          // Tylko OK = prawdziwe wyniki. ZERO_RESULTS i inne → null (spróbuj Photon)
          if (status === ZERO_RESULTS) { resolve(null); return; }
          if (status !== OK || !predictions?.length) { resolve(null); return; }
          const seen = new Set();
          const results = predictions
            .map((p) => {
              const streetName = p.structured_formatting?.main_text || p.description;
              // Usuń prefix "ulica ", "aleja " itp.
              const cleanName = streetName.replace(/^(ulica|aleja|aleje|plac|rondo|osiedle|droga)\s+/i, '');
              return { id: p.place_id, name: cleanName, displayName: cleanName, fullAddress: p.description };
            })
            .filter((r) => { if (seen.has(r.name)) return false; seen.add(r.name); return true; })
            .slice(0, 8);
          resolve(results.length > 0 ? results : null); // null = spróbuj Photon
        }
      );
    });
  } catch {
    return null; // fallback to Photon
  }
};

/**
 * Fetch streets using Photon API (komoot.io) + Nominatim fallback.
 */
const getStreetsPhoton = async (cityName, streetQuery = '') => {
  if (!cityName || streetQuery.length < 2) return [];

  const cacheKey = `streets_photon_${normalizePolishText(cityName)}_${normalizePolishText(streetQuery)}`;
  const cached = getCachedResult(cacheKey);
  if (cached && cached.length > 0) return cached;

  try {
    // NOTE: Photon supports only lang=default/de/en/fr (NOT pl), and does NOT support countrycodes.
    // Use bbox to restrict results to Poland.
    const url = `https://photon.komoot.io/api/?` + new URLSearchParams({
      q: `${streetQuery} ${cityName}`,
      limit: 20,
      osm_tag: 'highway',
      bbox: POLAND_BBOX,
    });

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Photon API error: ${response.status}`);

    const data = await response.json();
    const normalizedCity = normalizePolishText(cityName);
    const normalizedQuery = normalizePolishText(streetQuery);

    const results = (data.features || [])
      .filter((f) => {
        const props = f.properties || {};
        // Must be a highway/road feature
        if (props.osm_key !== 'highway') return false;
        // Must have a name
        if (!props.name) return false;
        // Name must actually contain the typed query (filter out unrelated results)
        if (!normalizePolishText(props.name).includes(normalizedQuery)) return false;
        // Must be in Poland
        if (props.countrycode && props.countrycode !== 'PL') return false;
        // City check — if Photon returned a city field, it must match our city
        const featureCity = props.city || props.town || props.village || '';
        if (featureCity && !normalizePolishText(featureCity).includes(normalizedCity)) return false;
        return true;
      })
      .map((f) => ({
        id: f.properties.osm_id || f.properties.name,
        name: f.properties.name,
        displayName: f.properties.name,
        fullAddress: [f.properties.name, f.properties.city || f.properties.town || cityName].filter(Boolean).join(', '),
      }))
      .filter((s, idx, arr) =>
        arr.findIndex((x) => normalizePolishText(x.name) === normalizePolishText(s.name)) === idx
      )
      .sort((a, b) => {
        const q = normalizedQuery;
        const aStarts = normalizePolishText(a.name).startsWith(q);
        const bStarts = normalizePolishText(b.name).startsWith(q);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.name.localeCompare(b.name, 'pl');
      })
      .slice(0, 10);

    if (results.length === 0) {
      const nominatimResults = await fetchStreetsNominatim(cityName, streetQuery);
      if (nominatimResults.length > 0) setCachedResult(cacheKey, nominatimResults);
      return nominatimResults;
    }

    setCachedResult(cacheKey, results);
    return results;
  } catch (error) {
    return fetchStreetsNominatim(cityName, streetQuery);
  }
};

/**
 * Fetch streets: Supabase (lokalna baza) → Google Places → Photon → Nominatim.
 * Priorytet 1: lokalna baza (pg_trgm) — ultra szybkie, bez latencji sieciowej
 * Priorytet 2: Google Places — najlepsza jakość gdy brak w bazie
 * Priorytet 3: Photon + Nominatim — fallback
 */
export const getStreets = async (cityName, streetQuery = '') => {
  if (!cityName || streetQuery.length < 2) return [];

  // 1. Supabase lokalna baza — najszybsza (~20-50ms)
  const localResults = await getStreetsLocal(cityName, streetQuery);
  if (localResults !== null && localResults.length > 0) return localResults;

  // 2. Google Places — najlepsza jakość (zwraca null gdy brak → próbuj dalej)
  if (GOOGLE_API_KEY) {
    const googleResults = await getStreetsGoogle(cityName, streetQuery);
    if (googleResults !== null) return googleResults;
  }

  // 3. Photon (komoot) + Nominatim fallback
  return getStreetsPhoton(cityName, streetQuery);
};

// ─── Supabase lokalna baza dzielnic (najszybsza) ────────────────────────────
const getDistrictsLocal = async (cityName, query = '') => {
  const cacheKey = `districts_local_${normalizePolishText(cityName)}_${normalizePolishText(query)}`;
  const cached = getCachedResult(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase.rpc('search_districts', {
      p_city: cityName,
      p_query: query || '',
      p_limit: query ? 30 : 500,
    });

    if (error || !data) return null;

    const results = data.map(row => ({
      name: row.district_name,
      displayName: row.district_name,
      city: row.district_city,
    }));

    if (results.length > 0) {
      setCachedResult(cacheKey, results);
    }
    return results;
  } catch {
    return null;
  }
};

/**
 * Fetch districts/osiedla for a Polish city.
 * Primary: Supabase lokalna baza — ultra szybkie (~50ms).
 * Fallback: statyczna lista.
 */
export const fetchDistricts = async (cityName, query = '') => {
  if (!cityName) return [];

  // 1. Supabase lokalna baza — najszybsza
  const localResults = await getDistrictsLocal(cityName, query);
  if (localResults !== null && localResults.length > 0) return localResults;

  // 2. Fallback: statyczna lista
  const staticDistricts = getDistrictsForCity(cityName);

  if (query.length >= 2) {
    const q = normalizePolishText(query);
    return staticDistricts.filter(d => normalizePolishText(d.name).includes(q));
  }

  return staticDistricts;
};

/**
 * Debounce function to limit API calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
/**
 * Fetch cities using Nominatim (OSM)
 */
const fetchCitiesNominatim = async (query) => {
  
  try {
    const url = `https://nominatim.openstreetmap.org/search?` + new URLSearchParams({
      q: `${query}, Polska`,
      format: 'json',
      addressdetails: 1,
      limit: 15,
      countrycodes: 'pl',
      'accept-language': 'pl'
    });
    
    
    const response = await fetch(url);

    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    
    const filtered = data
      .filter(item => {
        // Sprawdź różne typy miejscowości
        const cityName = item.address?.city || 
                        item.address?.town || 
                        item.address?.village ||
                        item.address?.administrative; // dla Warszawy!
        
        const isValidCity = cityName && 
                           item.address?.country === 'Polska' &&
                           // Filtruj tylko prawdziwe miasta/miejscowości
                           (item.class === 'boundary' || item.class === 'place') &&
                           (item.type === 'administrative' || item.type === 'city' || item.type === 'town' || item.type === 'village');
        
        return isValidCity;
      })
      .map(item => {
        const cityName = item.address.city || 
                        item.address.town || 
                        item.address.village ||
                        item.address.administrative ||
                        item.name; // fallback do nazwy miejsca
                        
        const result = {
          name: cityName,
          displayName: cityName,
          coordinates: {
            lat: parseFloat(item.lat),
            lon: parseFloat(item.lon)
          }
        };
        return result;
      })
      .slice(0, 15);
      
    return filtered;
      
  } catch (error) {
    return [];
  }
};

/**
 * Fallback function for streets using Nominatim when no Google API key
 */
const fetchStreetsNominatim = async (cityName, streetQuery) => {
  
  try {
    const url = `https://nominatim.openstreetmap.org/search?` + new URLSearchParams({
      q: `${streetQuery}, ${cityName}, Polska`,
      format: 'json',
      addressdetails: 1,
      limit: 10,
      countrycodes: 'pl',
      'accept-language': 'pl'
    });
    
    
    const response = await fetch(url);

    if (!response.ok) return [];
    
    const data = await response.json();
    
    
    const filtered = data
      .filter(item => {
        const streetName = item.address?.road;
        const itemCity = item.address?.city || item.address?.town || item.address?.village;
        const normalizedItemCity = normalizePolishText(itemCity || '');
        const normalizedSearchCity = normalizePolishText(cityName);
        
        
        // Sprawdź czy ulica jest w odpowiednim mieście i czy to highway
        const cityMatches = normalizedItemCity === normalizedSearchCity;
        const isHighway = item.class === 'highway';
        
        
        return streetName && itemCity && cityMatches && isHighway;
      })
      .map((item, index) => ({
        id: `${item.place_id || index}`, // Unikalne ID dla React keys
        name: item.address.road,
        displayName: item.address.road,
        fullAddress: item.display_name,
        coordinates: {
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon)
        }
      }))
      // Usuń duplikaty na podstawie nazwy ulicy
      .filter((street, index, array) => 
        array.findIndex(s => 
          normalizePolishText(s.name) === normalizePolishText(street.name)
        ) === index
      )
      .slice(0, 10);
      
    return filtered;
      
  } catch (error) {
    return [];
  }
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Get popular Polish cities for default suggestions (like Gratka.pl)
 * @returns {Array} - Array of popular cities
 */
export const getPopularCities = (query = '') => {
  const allCities = [
    'Warszawa',
    'Kraków', 
    'Łódź',
    'Wrocław',
    'Poznań',
    'Gdańsk',
    'Szczecin',
    'Bydgoszcz',
    'Lublin',
    'Białystok',
    'Katowice',
    'Gdynia',
    'Częstochowa',
    'Radom',
    'Sosnowiec',
    'Toruń',
    'Kielce',
    'Gliwice',
    'Zabrze',
    'Bytom',
    'Olsztyn',
    'Bielsko-Biała',
    'Rzeszów',
    'Ruda Śląska',
    'Rybnik',
    'Dąbrowa Górnicza',
    'Tychy',
    'Płock',
    'Elbląg',
    'Opole',
    'Wałbrzych',
    'Gorzów Wielkopolski',
    'Włocławek',
    'Tarnów',
    'Chorzów',
    'Koszalin',
    'Kalisz',
    'Legnica',
    'Grudziądz',
    'Słupsk',
    'Jaworzno',
    'Jastrzębie-Zdrój',
    'Nowy Sącz',
    'Jelenia Góra',
    'Siedlce',
    'Mysłowice',
    'Konin',
    'Piła',
    'Inowrocław',
    'Lubin',
    'Ostrowiec Świętokrzyski',
    'Gniezno',
    'Stargard',
    'Suwałki',
    'Głogów',
    'Chełm',
    'Tomaszów Mazowiecki',
    'Leszno',
    'Zamość',
    'Żory',
    'Pułtusk',
    'Ostrów Wielkopolski',
    'Starachowice',
    'Piotrków Trybunalski',
    'Świdnica',
    'Będzin',
    'Zgierz',
    'Tczew',
    'Racibórz',
    'Zawiercie',
    'Wodzisław Śląski',
    'Mielec',
    'Puławy',
    'Stalowa Wola',
    'Kutno',
    'Pruszków',
    'Ełk',
    'Oława',
    'Sieradz',
    'Żary',
    'Świętochłowice',
    'Bolesławiec',
    'Przemyśl',
    'Skierniewice',
    'Sanok',
    'Ostróda',
    'Sopot',
    'Siemianowice Śląskie',
    'Kędzierzyn-Koźle',
    'Krosno',
    'Malbork',
    'Ciechanów',
    'Wieluń',
    'Starogard Gdański',
    'Zielona Góra',
    'Wejherowo',
    'Wieliczka',
    'Wadowice',
    'Wągrowiec',
    'Wolsztyn',
    'Wyszków',
    'Wołomin',
    'Wąbrzeźno',
    'Łomianki',
    'Piaseczno',
    'Legionowo',
    'Marki',
    'Ząbki',
    'Zielonka',
    'Kobyłka',
    'Józefów',
    'Sulejówek',
    'Konstancin-Jeziorna',
    'Grodzisk Mazowiecki',
    'Milanówek',
    'Brwinów',
    'Piastów',
    'Otwock',
    'Mińsk Mazowiecki',
    'Nowy Dwór Mazowiecki',
    'Jabłonna',
    'Izabelin',
    'Stare Babice',
    'Michałowice',
    'Raszyn',
    'Lesznowola',
    'Wiązowna',
    'Nadarzyn',
    'Łomża',
    'Nowy Targ',
    'Ostrów Mazowiecka',
    'Ostrołęka',
    'Żyrardów',
    'Biała Podlaska',
    'Śrem',
    'Września',
    'Kłodzko',
    'Nysa',
    'Brzeg',
    'Strzelce Opolskie',
    'Człuchów',
    'Chojnice',
    'Świecie',
    'Brodnica',
    'Lipno',
    'Włodawa',
    'Hrubieszów',
    'Kraśnik',
    'Świdnik',
    'Biłgoraj',
    'Łuków',
    'Radzyń Podlaski',
    'Augustów',
    'Grajewo',
    'Hajnówka',
    'Wysokie Mazowieckie',
    'Kolno',
    'Mońki',
    'Sokółka',
    'Pisz',
    'Giżycko',
    'Mrągowo',
    'Kętrzyn',
    'Lidzbark Warmiński',
    'Bartoszyce',
    'Iława',
    'Działdowo',
    'Nowe Miasto Lubawskie',
    'Golub-Dobrzyń',
    'Rypin',
    'Lipno',
    'Aleksandrów Kujawski',
    'Żnin',
    'Nakło nad Notecią',
    'Szubin',
    'Wyrzysk',
    'Chodzież',
    'Czarnków',
    'Trzcianka',
    'Złotów',
    'Kościerzyna',
    'Kartuzy',
    'Reda',
    'Rumia',
    'Pruszcz Gdański',
    'Tczew',
    'Kwidzyn',
    'Nowy Dwór Gdański',
    'Lębork',
    'Bytów',
    'Miastko',
    'Sławno',
    'Darłowo',
    'Sianów',
    'Białogard',
    'Świdwin',
    'Drawsko Pomorskie',
    'Szczecinek',
    'Złocieniec',
    'Choszczno',
    'Myślibórz',
    'Gryfino',
    'Police',
    'Świnoujście',
    'Kamień Pomorski',
    'Dziwnów',
    'Kołobrzeg',
    'Trzebiatów',
    'Gryfice',
    'Nowogard',
    'Goleniów',
    'Stargard',
    'Pyrzyce',
    'Dębno',
    'Kostrzyn nad Odrą',
    'Słubice',
    'Sulęcin',
    'Rzepin',
    'Nowa Sól',
    'Żagań',
    'Żary',
    'Lubsko',
    'Szprotawa',
    'Polkowice',
    'Chojnów',
    'Złotoryja',
    'Kamienna Góra',
    'Dzierżoniów',
    'Bielawa',
    'Pieszyce',
    'Kłodzko',
    'Nowa Ruda',
    'Bystrzyca Kłodzka',
    'Ząbkowice Śląskie',
    'Strzelin',
    'Ziębice',
    'Brzeg',
    'Namysłów',
    'Kluczbork',
    'Olesno',
    'Lubliniec',
    'Tarnowskie Góry',
    'Piekary Śląskie',
    'Mikołów',
    'Łaziska Górne',
    'Orzesze',
    'Pszczyna',
    'Bieruń',
    'Lędziny',
    'Czechowice-Dziedzice',
    'Cieszyn',
    'Ustroń',
    'Wisła',
    'Żywiec',
    'Sucha Beskidzka',
    'Andrychów',
    'Oświęcim',
    'Chrzanów',
    'Jaworzno',
    'Olkusz',
    'Miechów',
    'Proszowice',
    'Bochnia',
    'Brzesko',
    'Dąbrowa Tarnowska',
    'Mielec',
    'Ropczyce',
    'Dębica',
    'Jasło',
    'Krosno',
    'Lesko',
    'Ustrzyki Dolne',
    'Przemyśl',
    'Jarosław',
    'Przeworsk',
    'Łańcut',
    'Leżajsk',
    'Nisko',
    'Tarnobrzeg',
    'Sandomierz',
    'Ostrowiec Świętokrzyski',
    'Starachowice',
    'Skarżysko-Kamienna',
    'Końskie',
    'Opoczno',
    'Tomaszów Mazowiecki',
    'Radomsko',
    'Bełchatów',
    'Zduńska Wola',
    'Sieradz',
    'Wieluń',
    'Wieruszów',
    'Kępno',
    'Ostrzeszów',
    'Krotoszyn',
    'Jarocin',
    'Pleszew',
    'Turek',
    'Koło',
    'Słupca',
    'Środa Wielkopolska',
    'Szamotuły',
    'Nowy Tomyśl',
    'Śrem',
    'Gostyń',
    'Rawicz',
    'Góra',
    'Wschowa'
  ]
  // Usuń duplikaty (zachowaj pierwszą występującą nazwę)
  .filter((city, idx, arr) => arr.indexOf(city) === idx)
  .map(city => ({ 
    name: city, 
    displayName: city,
    coordinates: null 
  }));

  // Jeśli nie ma query, zwróć wszystkie miasta (MUI filterOptions zajmie się filtrowaniem)
  if (!query || query.length === 0) {
    return allCities;
  }

  // Filtruj case-insensitive
  const normalizedQuery = normalizePolishText(query);

  const filtered = allCities.filter(city => {
    const normalizedCityName = normalizePolishText(city.name);
    return normalizedCityName.startsWith(normalizedQuery);
  });

  // Sortuj - najpierw te które zaczynają się od query
  const sorted = filtered.sort((a, b) => {
    const aStartsWith = normalizePolishText(a.name).startsWith(normalizedQuery);
    const bStartsWith = normalizePolishText(b.name).startsWith(normalizedQuery);
    
    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    
    return a.name.localeCompare(b.name, 'pl');
  });

  return sorted;
};

/**
 * Get popular districts for Polish cities
 * @param {string} cityName - Name of the city
 * @returns {Array} - Array of district objects
 */
export const getDistrictsForCity = (cityName) => {
  if (!cityName) return [];
  
  const normalizedCity = normalizePolishText(cityName);
  
  const districtsMap = {
    warszawa: [
      'Śródmieście',
      'Mokotów',
      'Ochota',
      'Wola',
      'Żoliborz',
      'Praga-Północ',
      'Praga-Południe',
      'Targówek',
      'Bemowo',
      'Bielany',
      'Ursynów',
      'Wilanów',
      'Włochy',
      'Ursus',
      'Białołęka',
      'Wawer',
      'Wesołą'
    ],
    krakow: [
      'Stare Miasto',
      'Kazimierz',
      'Podgórze',
      'Krowodrza',
      'Prądnik Czerwony',
      'Prądnik Biały',
      'Nowa Huta',
      'Mistrzejowice',
      'Bieńczyce',
      'Wzgórza Krzesławickie',
      'Swoszowice',
      'Łagiewniki-Borek Fałęcki',
      'Dębniki',
      'Bronowice',
      'Grzegórzki'
    ],
    gdansk: [
      'Śródmieście',
      'Stare Miasto',
      'Wrzeszcz',
      'Oliwa',
      'Zaspa',
      'Przymorze',
      'Jelitkowo',
      'Piecki-Migowo',
      'Chełm',
      'Siedlce',
      'Rudniki',
      'Kokoszki',
      'Osowa'
    ],
    wroclaw: [
      'Stare Miasto',
      'Śródmieście',
      'Krzyki',
      'Fabryczna',
      'Psie Pole',
      'Karłowice',
      'Gaj',
      'Pilczyce',
      'Borek',
      'Pawłowice',
      'Muchobór Wielki',
      'Popowice',
      'Ołtaszyn'
    ],
    poznan: [
      'Stare Miasto',
      'Wilda',
      'Jeżyce',
      'Grunwald',
      'Łazarz',
      'Rataje',
      'Winogrady',
      'Piątkowo',
      'Chartowo',
      'Naramowice',
      'Górczyn',
      'Malta'
    ],
    lodz: [
      'Śródmieście',
      'Bałuty',
      'Górna',
      'Polesie',
      'Widzew',
      'Retkinia',
      'Chojny',
      'Ruda',
      'Dąbrowa'
    ],
    katowice: [
      'Śródmieście',
      'Załęże',
      'Wełnowiec-Józefowiec',
      'Ochojec',
      'Ligota-Panewniki',
      'Piotrowice-Ochojec',
      'Kostuchna',
      'Murcki',
      'Giszowiec',
      'Janów-Nikiszowiec',
      'Bogucice',
      'Brynów-Osiedle Zgrzebnioka'
    ],
    szczecin: [
      'Śródmieście',
      'Centrum',
      'Pogodno',
      'Gumieńce',
      'Niebuszewo',
      'Świerczewo',
      'Arkońskie-Niemierzyn',
      'Dąbie',
      'Żelechowa',
      'Turzyn'
    ],
    bydgoszcz: [
      'Śródmieście',
      'Fordon',
      'Wyżyny',
      'Szwederowo',
      'Osowa Góra',
      'Kapuściska',
      'Błonie'
    ],
    lublin: [
      'Śródmieście',
      'Bronowice',
      'Czuby',
      'Kalinowszczyzna',
      'LSM',
      'Ponikwoda',
      'Sławin',
      'Tatary',
      'Węglin',
      'Wieniawa'
    ]
  };
  
  const districts = districtsMap[normalizedCity] || [];
  
  return districts.map(district => ({
    name: district,
    displayName: district,
    city: cityName
  }));
};

